import { useCallback, useEffect, useState } from 'react';

import type { CatalogueDesign } from '@/api/masters';
import { DesignPhoto } from '@/components/DesignPhoto';
import { Button } from '@/components/PageHeading';
import { useEscapeKey } from '@/components/shortcuts';
import { StatusBadge } from '@/components/StatusBadge';
import {
  designPage,
  garmentAvailability,
  garmentWear,
  optionAvailability,
  type DesignOption,
} from '@core/bookings/catalogue';
import { slabLabel } from '@core/bookings/pricing';
import { formatAmount } from '@core/format/money';
import type { TagRateLine } from '@core/print/hangTag';
import { withReference } from '@core/units/referenceNumber';

/**
 * One design's page: its picture, the looks it comes in, the sizes of the chosen look with what is
 * free on the dates, the price, and which garment the shop would set aside — the proposal's design
 * page, as a popup over the cards.
 *
 * <p>
 * <strong>A popup, not a column beside the cards</strong> (owner's call, 2026-10-10): the column
 * squeezed the grid to three across and sat a long scroll away on a phone. Here the picture takes
 * the left half on a desk with the choices beside it, and the two stack on a phone. It writes
 * nothing, so the insert/update rule does not apply. It follows the overlay rules: the overlay
 * tokens, `data-shortcut-layer`, Esc, and mounted in the page.
 * </p>
 *
 * <p>
 * <strong>Every figure is the core's</strong> (`@core/bookings/catalogue`, `pricing`), so the
 * phone's design page cannot disagree with it. A taken size says when it comes back rather than
 * disappearing: "no, but it is back on the 24th" is the answer a counter gives most.
 * </p>
 *
 * <p>
 * The picture follows the look: once a colour is chosen, the first garment of it with a picture of
 * its own (or a sibling's) is shown, so maroon is not described with a photograph of the gold.
 * </p>
 *
 * <p>
 * <strong>With `pick` it is the booking counter's design page</strong>: a garment is set aside
 * automatically (`pickGarment`), the clerk may tap another free one instead, and **Add for …**
 * books exactly that garment. Garments already in the booking count as taken, so the choice moves on
 * to the next one rather than offering the same garment twice. Booking by size still books one
 * garment — the line, its availability and its hang tag are unchanged.
 * </p>
 */
export function DesignModal({
  design,
  days,
  currency,
  onClose,
  pick,
}: {
  design: CatalogueDesign;
  days: number;
  currency: string;
  onClose: () => void;
  /** Present on the booking counter: who the garment is for, and how to add it. */
  pick?:
    | {
        /** The wearer an Add goes to; null while nobody is chosen. */
        forName: string | null;
        /** Garments already in the booking being taken. */
        claimed: ReadonlySet<number>;
        disabled?: boolean;
        /**
         * What the button says in place of "Add for …" — "Swap SHW-40-01 for this" while the
         * booking is replacing a garment rather than adding one.
         */
        actionLabel?: string | undefined;
        onAdd: (unitId: number) => void;
      }
    | undefined;
}) {
  // What was tapped; `designPage` decides what is in force. The caller keys this popup on the
  // design, so a different design starts afresh with no effect needed to reset it.
  const [chosenLook, setLook] = useState<string | undefined>();
  const [chosenSize, setSize] = useState<string | undefined>();
  const [chosenGarment, setGarment] = useState<number | undefined>();
  const page = designPage(
    design,
    days,
    { look: chosenLook, size: chosenSize, garment: chosenGarment },
    currency,
    pick?.claimed,
  );
  const { look, size, setAside, priced, charge } = page;

  useEscapeKey(onClose);

  // The page behind must not scroll while this is open, or closing it lands the clerk somewhere
  // else in sixty designs — MediaViewer's rule.
  useEffect(() => {
    const previous = document.body.style.overflow;

    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  // Focus moves into the popup so Tab walks its chips rather than the cards behind it. A ref
  // callback, for `useAutoFocusFirstField`'s reason; a button raises no on-screen keyboard.
  const focusClose = useCallback((button: HTMLButtonElement | null) => {
    button?.focus({ preventScroll: true });
  }, []);

  return (
    <div
      data-shortcut-layer
      className="fixed inset-0 z-9999 flex items-center justify-center p-4 max-sm:p-2 print:hidden"
    >
      <button
        type="button"
        aria-label="Close"
        tabIndex={-1}
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-[var(--overlay-scrim)]"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="design-modal-title"
        className="relative grid max-h-[calc(100dvh-2rem)] w-full max-w-[960px] overflow-y-auto overscroll-contain rounded-lg border border-[var(--overlay-border)] bg-[var(--overlay-surface)] shadow-lg max-sm:max-h-[calc(100dvh-1rem)] md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]"
      >
        <button
          ref={focusClose}
          type="button"
          onClick={onClose}
          aria-label="Close this design"
          className="absolute end-2 top-2 z-10 flex size-8 cursor-pointer items-center justify-center rounded-full bg-white/90 text-gray-900 shadow hover:bg-white"
        >
          <i className="icon icon-x text-[14px]" />
        </button>

        {/* Sticky on a desk, so the picture stays in view while a long size list scrolls. */}
        <div className="md:sticky md:top-0 md:self-start">
          <DesignPhoto
            url={page.picture}
            alt={design.itemName}
            categoryPath={design.categoryPath}
            className="max-md:max-h-72"
          />
        </div>

        <div className="flex flex-col gap-4 p-4 md:p-5">
          <div className="pe-8">
            <p className="text-default mb-0 text-[length:var(--scale-list-meta)] tracking-wide uppercase">
              {design.categoryPath}
            </p>
            <h2 id="design-modal-title" className="mb-0 text-lg font-bold text-gray-900">
              {design.itemName}
            </h2>
            <p className="text-default font-mono-nums mb-0 text-[length:var(--scale-list-meta)]">
              {design.itemCode}
              {design.turnaroundBufferDays > 0 &&
                ` · ${design.turnaroundBufferDays} day${design.turnaroundBufferDays === 1 ? '' : 's'} to clean after return`}
            </p>
          </div>

          {page.showLooks ? (
            <Choices
              label="Colour and look"
              options={page.looks}
              chosen={look}
              onChoose={setLook}
            />
          ) : null}

          <Choices
            label="Size for these dates"
            options={page.sizes}
            chosen={size}
            onChoose={setSize}
          />

          <div>
            <Label>
              Price for {days} day{days === 1 ? '' : 's'}
            </Label>
            {charge ? (
              <p className="mb-0 text-[length:var(--scale-list-name)]">
                <span className="font-mono-nums text-lg font-bold text-gray-900">
                  {currency}
                  {formatAmount(charge.amount)}
                </span>
                <span className="text-default ms-2">{slabLabel(charge)}</span>
              </p>
            ) : (
              <StatusBadge tone="warning">No rate for this garment</StatusBadge>
            )}
            {priced && page.ladder.length > 0 && <Ladder lines={page.ladder} />}
          </div>

          <div>
            <Label>Garments of this size</Label>
            {pick && page.pick && page.overridden ? (
              <p className="text-default mb-2 text-[length:var(--scale-list-meta)]">
                You chose{' '}
                <b className="font-mono-nums text-gray-900">
                  {withReference(page.pick.serialCode, page.pick.referenceNumber)}
                </b>
                .{' '}
                <button
                  type="button"
                  onClick={() => setGarment(undefined)}
                  className="text-primary cursor-pointer font-semibold"
                >
                  Use the automatic choice
                </button>
              </p>
            ) : pick && setAside ? (
              <p className="text-default mb-2 text-[length:var(--scale-list-meta)]">
                Booking by size sets aside{' '}
                <b className="font-mono-nums text-gray-900">
                  {withReference(setAside.serialCode, setAside.referenceNumber)}
                </b>{' '}
                — free, in the best condition, rented least. Tap another free one to choose it
                instead.
              </p>
            ) : setAside ? (
              <p className="text-default mb-2 text-[length:var(--scale-list-meta)]">
                Booking by size sets aside{' '}
                <b className="font-mono-nums text-gray-900">
                  {withReference(setAside.serialCode, setAside.referenceNumber)}
                </b>{' '}
                — free, in the best condition, rented least.
              </p>
            ) : (
              <p className="text-default mb-2 text-[length:var(--scale-list-meta)]">
                None of this size is free on these dates.
              </p>
            )}
            <ul className="divide-border-color flex flex-col divide-y ps-0">
              {page.garments.map((unit) => {
                const state = garmentAvailability(unit, pick?.claimed.has(unit.unitId) ?? false);
                const choosable = pick !== undefined && unit.free;
                const chosen = choosable && page.pick?.unitId === unit.unitId;

                return (
                  <li
                    key={unit.unitId}
                    className="flex list-none items-center justify-between gap-2 py-1.5"
                  >
                    {choosable && (
                      <input
                        type="radio"
                        name="design-modal-garment"
                        checked={chosen}
                        onChange={() => setGarment(unit.unitId)}
                        aria-label={`Book ${unit.serialCode}`}
                        className="accent-primary size-4 shrink-0 cursor-pointer"
                      />
                    )}
                    <span className="min-w-0 flex-1">
                      <span className="font-mono-nums block truncate text-[length:var(--scale-list-name)] font-semibold text-gray-900">
                        {withReference(unit.serialCode, unit.referenceNumber)}
                      </span>
                      <span className="text-default text-[length:var(--scale-list-meta)]">
                        {garmentWear(unit)}
                      </span>
                    </span>
                    <StatusBadge tone={state.tone}>{state.label}</StatusBadge>
                  </li>
                );
              })}
            </ul>
          </div>

          {pick && (
            // Pinned to the foot of the popup, so Add stays in reach however long the list runs.
            <div className="border-border-color sticky bottom-0 -mx-4 -mb-4 flex flex-wrap items-center justify-between gap-2 border-t bg-[var(--overlay-surface)] px-4 py-3 md:-mx-5 md:-mb-5 md:px-5">
              <span className="text-default text-[length:var(--scale-list-meta)]">
                {pick.forName === null
                  ? 'Choose who it is for on the booking first.'
                  : page.pick
                    ? `${withReference(page.pick.serialCode, page.pick.referenceNumber)}${charge ? ` · ${currency}${formatAmount(charge.amount)}` : ''}`
                    : 'Nothing of this size is free on these dates.'}
              </span>
              <Button
                variant="primary"
                disabled={pick.disabled || pick.forName === null || page.pick === null}
                onClick={() => page.pick && pick.onAdd(page.pick.unitId)}
              >
                {pick.actionLabel ?? `Add for ${pick.forName || 'the wearer'}`}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-default mb-1.5 text-[length:var(--scale-list-meta)] font-semibold tracking-wide uppercase">
      {children}
    </p>
  );
}

/**
 * A row of chips — looks or sizes — each saying what is free. A taken choice stays pickable, so its
 * garments and its return date can be seen; it is drawn quieter and says when it is back.
 */
function Choices({
  label,
  options,
  chosen,
  onChoose,
}: {
  label: string;
  options: DesignOption[];
  chosen: string | undefined;
  onChoose: (key: string) => void;
}) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const on = option.key === chosen;

          return (
            <button
              key={option.key}
              type="button"
              aria-pressed={on}
              onClick={() => onChoose(option.key)}
              className={`flex min-w-16 cursor-pointer flex-col items-center rounded-md border px-3 py-1.5 ${
                on ? 'border-primary bg-primary-transparent' : 'border-border-color bg-white'
              } ${option.freeCount === 0 ? 'opacity-70' : ''}`}
            >
              <span className="flex items-center gap-1.5 text-[length:var(--scale-list-name)] font-semibold text-gray-900">
                {option.colourHex && (
                  <span
                    aria-hidden="true"
                    className="border-border-color size-3 rounded-full border"
                    style={{ backgroundColor: option.colourHex }}
                  />
                )}
                {option.label || 'One size'}
              </span>
              <span
                className={`text-[length:var(--scale-list-meta)] ${on ? 'text-primary' : 'text-default'}`}
              >
                {optionAvailability(option)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The card's bands as a shop quotes them — "Up to 3 days ₹4,500 · Each extra day ₹600" — in the
 * hang tag's own words and the pricer's own figures, so the screen and the tag on the garment never
 * quote one card two ways. Every band is shown; only the tag runs out of room.
 */
function Ladder({ lines }: { lines: TagRateLine[] }) {
  return (
    <div className="border-border-color mt-2 flex overflow-hidden rounded-md border">
      {lines.map((line) => (
        <div
          key={line.label}
          className="border-border-color flex-1 border-s px-2 py-1 text-center first:border-s-0"
        >
          <span className="font-mono-nums block text-[length:var(--scale-list-name)] font-semibold text-gray-900">
            {line.value}
          </span>
          <span className="text-default text-[length:var(--scale-list-meta)]">{line.label}</span>
        </div>
      ))}
    </div>
  );
}
